import { API_BASE } from '../../config/apiBase';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { 
  DollarSign,
  Plus,
  ArrowRight,
  Search,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API = API_BASE;

const PricingList = () => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${API}/deal-structures`);
      setDeals(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDeals = deals.filter(d =>
    d.client_name?.toLowerCase().includes(search.toLowerCase()) ||
    d.tier_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pricing-list-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-amber-600" />
            Precios y Estructura
          </h1>
          <p className="text-muted-foreground">Estructuración de deals y escenarios</p>
        </div>
        <Button onClick={() => navigate('/apps/pricing/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Deal
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Buscar por cliente o tier..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {filteredDeals.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDeals.map((deal) => (
            <Card 
              key={deal.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/apps/pricing/${deal.id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'w-12 h-12 rounded-lg flex items-center justify-center',
                      deal.is_valid ? 'bg-amber-100' : 'bg-red-100'
                    )}>
                      <DollarSign className={cn(
                        'w-6 h-6',
                        deal.is_valid ? 'text-amber-600' : 'text-red-600'
                      )} />
                    </div>
                    <div>
                      <CardTitle className="text-base">{deal.tier_name || `Tier ${deal.tier}`}</CardTitle>
                      <CardDescription>{deal.client_name || 'Sin cliente'}</CardDescription>
                    </div>
                  </div>
                  {deal.is_valid ? (
                    <Badge variant="default" className="flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Válido
                    </Badge>
                  ) : (
                    <Badge variant="destructive" className="flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Revisar
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">Financiado</p>
                    <p className="font-medium text-amber-600">${deal.amount_financed?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Pago Mensual</p>
                    <p className="font-medium">${deal.monthly_payment?.toLocaleString()}</p>
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  {deal.term_months} meses \u2022 {deal.interest_rate}% \u2022 LTV {deal.ltv?.toFixed(1)}%
                </div>
                <div className="flex items-center text-sm text-primary font-medium pt-2">
                  Ver detalles
                  <ArrowRight className="w-4 h-4 ml-1" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="py-12">
          <CardContent className="text-center">
            <DollarSign className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No hay deals</h3>
            <p className="text-muted-foreground mb-4">Comienza creando tu primer deal structure</p>
            <Button onClick={() => navigate('/apps/pricing/new')}>
              <Plus className="w-4 h-4 mr-2" /> Nuevo Deal
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PricingList;
